import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useWallet } from '../../contexts/WalletContext';
import JupiterStyleTradingInterface from './JupiterStyleTradingInterface';
import PriceMonitor from './PriceMonitor';
import PriceChart from './PriceChart';
import Sidebar from '../layout/Sidebar';
import EnhancedDashboard from '../dashboard/EnhancedDashboard';
import WalletSelector from '../common/WalletSelector';
import MobileMenuToggle from '../common/MobileMenuToggle';
import { CopyableAddress, CopyableMint } from '../common/CopyButton';
import '../../styles/trading-dashboard.css';
import '../../styles/enhanced-dashboard.css';
import '../../styles/sidebar.css';
import '../../styles/wallet-selector.css';
import '../../styles/mobile-menu-toggle.css';

const TradingDashboard = ({ hideGlobalSidebar = false, setCurrentView }) => {
  const { tokens } = useAuth();
  const { wallets, activeWallet } = useWallet();
  const { showSuccess, showError, showTransaction } = useToast();
  
  // Debug logging
  console.log('🚀 TradingDashboard rendering with new components:', {
    Sidebar: !!Sidebar,
    EnhancedDashboard: !!EnhancedDashboard,
    WalletSelector: !!WalletSelector,
    MobileMenuToggle: !!MobileMenuToggle,
    activeWallet: activeWallet?.id,
    walletsCount: wallets?.length
  });
  
  // State for different sections
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, manual, automated, sessions, alerts
  const [loading, setLoading] = useState(false);
  
  // Real-time dashboard state
  const [realTimeData, setRealTimeData] = useState({
    totalPnL: 0,
    activeTrades: 0,
    todayVolume: 0,
    successRate: 0
  });
  const [priceUpdates, setPriceUpdates] = useState(new Map());
  const [systemStatus, setSystemStatus] = useState({
    tradingEngine: 'active',
    priceFeeds: 'connected',
    lastUpdate: new Date()
  });
  
  // Manual trading state
  const [manualTrade, setManualTrade] = useState({
    tokenMint: '',
    tradeType: 'buy',
    amount: '',
    slippage: 0.5
  });
  const [quote, setQuote] = useState(null);
  
  // Automated trading state
  const [botConfig, setBotConfig] = useState({
    tokenMint: '',
    initialAmount: '',
    stopLossPercentage: 10,
    trailingStopEnabled: true,
    walletId: ''
  });
  const [activeSessions, setActiveSessions] = useState([]);
  
  // Alerts state
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Trading history
  const [tradeHistory, setTradeHistory] = useState([]);
  
  // Session details modal
  const [selectedSessionDetails, setSelectedSessionDetails] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // WebSocket connection for real-time updates
  const wsRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumpbackend.urion.live/api';
  const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'wss://safepumpbackend.urion.live/ws';

  useEffect(() => {
    if (tokens?.access_token) {
      fetchActiveSessions();
      fetchTradeHistory();
      fetchAlerts();
      fetchRealTimeData();
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [tokens]);

  // WebSocket connection for real-time updates
  const connectWebSocket = () => {
    if (!tokens?.access_token) return;

    try {
      const ws = new WebSocket(WS_BASE_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('🔌 Trading Dashboard WebSocket connected');
        setWsConnected(true);
        
        // Authenticate
        ws.send(JSON.stringify({
          type: 'authenticate',
          token: tokens.access_token
        }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        console.log('🔌 Trading Dashboard WebSocket disconnected');
        setWsConnected(false);
        
        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          if (tokens?.access_token) {
            connectWebSocket();
          }
        }, 5000);
      };

      ws.onerror = (error) => {
        console.error('Trading Dashboard WebSocket error:', error);
        setWsConnected(false);
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
    }
  };

  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'authenticated':
        console.log('✅ Trading Dashboard WebSocket authenticated');
        // Subscribe to active session price updates
        activeSessions.forEach(session => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
              type: 'subscribe_price',
              tokenMint: session.tokenMint
            }));
          }
        });
        break;

      case 'price_update':
        setPriceUpdates(prev => new Map(prev.set(message.tokenMint, message.data)));
        updateSessionPrices(message.tokenMint, message.data);
        break;

      case 'trading_alert':
        // Handle real-time trading alerts
        setAlerts(prev => [message.data, ...prev]);
        setUnreadCount(prev => prev + 1);
        break;

      case 'session_update':
        // Handle real-time session updates
        updateSessionStatus(message.data);
        break;

      default:
        break;
    }
  };

  const updateSessionPrices = (tokenMint, priceData) => {
    setActiveSessions(prev => prev.map(session => {
      if (session.tokenMint === tokenMint) {
        const newProfitLoss = session.initialPrice ? 
          ((priceData.price - session.initialPrice) / session.initialPrice) * 100 : 0;
        
        return {
          ...session,
          currentPrice: priceData.price,
          profitLoss: newProfitLoss,
          lastUpdated: new Date()
        };
      }
      return session;
    }));
  };

  const updateSessionStatus = (sessionData) => {
    setActiveSessions(prev => prev.map(session => 
      session.sessionId === sessionData.sessionId ? { ...session, ...sessionData } : session
    ));
  };

  const fetchRealTimeData = async () => {
    try {
      const [pnlResponse, volumeResponse, statsResponse] = await Promise.all([
        makeAuthenticatedRequest('/trading/portfolio-summary'),
        makeAuthenticatedRequest('/trading/volume-stats'),
        makeAuthenticatedRequest('/trading/performance-stats')
      ]);

      setRealTimeData({
        totalPnL: pnlResponse.data?.totalPnL || 0,
        activeTrades: activeSessions.length,
        todayVolume: volumeResponse.data?.todayVolume || 0,
        successRate: statsResponse.data?.successRate || 0
      });
    } catch (error) {
      console.error('Failed to fetch real-time data:', error);
    }
  };

  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // ============================================================================
  // MANUAL TRADING FUNCTIONS
  // ============================================================================

  const getQuote = async () => {
    if (!manualTrade.tokenMint || !manualTrade.amount || !activeWallet) {
      showError('Please fill in all fields and ensure wallet is connected');
      return;
    }

    try {
      setLoading(true);
      
      const SOL_MINT = 'So11111111111111111111111111111111111111112';
      const inputMint = manualTrade.tradeType === 'buy' ? SOL_MINT : manualTrade.tokenMint;
      const outputMint = manualTrade.tradeType === 'buy' ? manualTrade.tokenMint : SOL_MINT;
      const amountLamports = manualTrade.tradeType === 'buy' 
        ? Math.floor(parseFloat(manualTrade.amount) * 1e9)
        : Math.floor(parseFloat(manualTrade.amount) * 1e6);

      const data = await makeAuthenticatedRequest('/trading/quote', {
        method: 'POST',
        body: JSON.stringify({
          inputMint,
          outputMint,
          amount: amountLamports,
          slippageBps: Math.floor(manualTrade.slippage * 100)
        })
      });

      setQuote(data.data.quote);
      showSuccess('Quote retrieved successfully');
    } catch (error) {
      showError(`Failed to get quote: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const executeManualTrade = async () => {
    if (!quote || !activeWallet) {
      showError('Please get a quote first and ensure wallet is connected');
      return;
    }

    try {
      setLoading(true);

      const endpoint = manualTrade.tradeType === 'buy' ? '/trading/manual-buy' : '/trading/manual-sell';
      const payload = manualTrade.tradeType === 'buy' 
        ? {
            tokenMint: manualTrade.tokenMint,
            solAmount: parseFloat(manualTrade.amount),
            walletId: activeWallet.id
          }
        : {
            tokenMint: manualTrade.tokenMint,
            tokenAmount: parseFloat(manualTrade.amount),
            walletId: activeWallet.id
          };

      const data = await makeAuthenticatedRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      // Reset form and refresh data
      setManualTrade({ ...manualTrade, amount: '' });
      setQuote(null);
      await fetchTradeHistory();
      
      showTransaction(manualTrade.tradeType, manualTrade.tokenMint, manualTrade.amount, data.data.transaction.signature);
    } catch (error) {
      showError(`Failed to execute trade: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // AUTOMATED TRADING FUNCTIONS
  // ============================================================================

  const startAutomatedTrading = async () => {
    if (!botConfig.tokenMint || !botConfig.initialAmount || !botConfig.walletId) {
      showError('Please fill in all bot configuration fields');
      return;
    }

    try {
      setLoading(true);

      const data = await makeAuthenticatedRequest('/trading/start-bot', {
        method: 'POST',
        body: JSON.stringify({
          tokenMint: botConfig.tokenMint,
          walletId: parseInt(botConfig.walletId),
          initialAmount: parseFloat(botConfig.initialAmount),
          stopLossPercentage: parseFloat(botConfig.stopLossPercentage),
          trailingStopEnabled: botConfig.trailingStopEnabled
        })
      });

      setBotConfig({ 
        tokenMint: '', 
        initialAmount: '', 
        stopLossPercentage: 10, 
        trailingStopEnabled: true, 
        walletId: '' 
      });
      await fetchActiveSessions();
      
      showSuccess(`Automated trading started for session ${data.data.session.sessionId}`);
    } catch (error) {
      showError(`Failed to start automated trading: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const stopAutomatedTrading = async (sessionId) => {
    try {
      setLoading(true);

      await makeAuthenticatedRequest(`/trading/stop-bot/${sessionId}`, {
        method: 'POST'
      });

      await fetchActiveSessions();
      showSuccess(`Trading session ${sessionId} stopped successfully`);
    } catch (error) {
      showError(`Failed to stop trading session: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveSessions = async () => {
    try {
      const data = await makeAuthenticatedRequest('/trading/active-bots');
      setActiveSessions(data.data.sessions);
    } catch (error) {
      console.error('Failed to fetch active sessions:', error);
    }
  };

  const fetchSessionDetails = async (sessionId) => {
    try {
      setLoading(true);
      const data = await makeAuthenticatedRequest(`/trading/bot-status/${sessionId}`);
      setSelectedSessionDetails(data.data.session);
      setShowSessionModal(true);
      return data.data.session;
    } catch (error) {
      showError(`Failed to fetch session details: ${error.message}`);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // DATA FETCHING FUNCTIONS
  // ============================================================================

  const fetchTradeHistory = async () => {
    try {
      const data = await makeAuthenticatedRequest('/trading/history?limit=20');
      setTradeHistory(data.data.trades);
    } catch (error) {
      console.error('Failed to fetch trade history:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const data = await makeAuthenticatedRequest('/trading/alerts');
      setAlerts(data.data.alerts);
      setUnreadCount(data.data.unreadCount);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      // Fallback to empty state
      setAlerts([]);
      setUnreadCount(0);
    }
  };

  const markAlertsAsRead = async (alertIds = null) => {
    try {
      await makeAuthenticatedRequest('/trading/alerts/mark-read', {
        method: 'POST',
        body: JSON.stringify({ alertIds })
      });
      
      // Refresh alerts after marking as read
      await fetchAlerts();
    } catch (error) {
      showError(`Failed to mark alerts as read: ${error.message}`);
    }
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const formatAmount = (amount, decimals = 6) => {
    return (amount / Math.pow(10, decimals)).toFixed(6);
  };

  const formatPercentage = (percent) => {
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(amount);
  };

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================

  const renderDashboard = () => (
    <div className="dashboard-overview">
      {/* System Status Bar */}
      <div className="system-status-bar">
        <div className="status-item">
          <span className="status-label">Trading Engine</span>
          <div className={`status-indicator ${systemStatus.tradingEngine}`}>
            {systemStatus.tradingEngine === 'active' ? '🟢' : '🔴'}
            <span>{systemStatus.tradingEngine}</span>
          </div>
        </div>
        <div className="status-item">
          <span className="status-label">Price Feeds</span>
          <div className={`status-indicator ${wsConnected ? 'connected' : 'disconnected'}`}>
            {wsConnected ? '🟢' : '🔴'}
            <span>{wsConnected ? 'connected' : 'disconnected'}</span>
          </div>
        </div>
        <div className="status-item">
          <span className="status-label">Last Update</span>
          <span className="status-time">
            {systemStatus.lastUpdate.toLocaleTimeString()}
          </span>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card pnl">
          <div className="metric-header">
            <h3>Total P&L</h3>
            <span className="metric-icon">💰</span>
          </div>
          <div className={`metric-value ${realTimeData.totalPnL >= 0 ? 'positive' : 'negative'}`}>
            {formatPercentage(realTimeData.totalPnL)}
          </div>
          <div className="metric-subtitle">
            All-time performance
          </div>
        </div>

        <div className="metric-card trades">
          <div className="metric-header">
            <h3>Active Trades</h3>
            <span className="metric-icon">🤖</span>
          </div>
          <div className="metric-value">
            {realTimeData.activeTrades}
          </div>
          <div className="metric-subtitle">
            Currently running bots
          </div>
        </div>

        <div className="metric-card volume">
          <div className="metric-header">
            <h3>Today's Volume</h3>
            <span className="metric-icon">📊</span>
          </div>
          <div className="metric-value">
            {formatCurrency(realTimeData.todayVolume)}
          </div>
          <div className="metric-subtitle">
            24h trading volume
          </div>
        </div>

        <div className="metric-card success">
          <div className="metric-header">
            <h3>Success Rate</h3>
            <span className="metric-icon">🎯</span>
          </div>
          <div className="metric-value">
            {realTimeData.successRate.toFixed(1)}%
          </div>
          <div className="metric-subtitle">
            Profitable trades ratio
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="action-buttons">
          <button 
            className="action-button start-bot"
            onClick={() => setActiveTab('automated')}
            disabled={!activeWallet}
          >
            <span className="action-icon">🚀</span>
            <div className="action-text">
              <strong>Start New Bot</strong>
              <p>Launch automated trading</p>
            </div>
          </button>
          
          <button 
            className="action-button manual-trade"
            onClick={() => setActiveTab('manual')}
            disabled={!activeWallet}
          >
            <span className="action-icon">⚡</span>
            <div className="action-text">
              <strong>Manual Trade</strong>
              <p>Execute instant trades</p>
            </div>
          </button>
          
          <button 
            className="action-button view-sessions"
            onClick={() => setActiveTab('sessions')}
          >
            <span className="action-icon">📈</span>
            <div className="action-text">
              <strong>View Sessions</strong>
              <p>Monitor active bots</p>
            </div>
          </button>
          
          <button 
            className="action-button emergency-stop"
            onClick={handleEmergencyStop}
            disabled={activeSessions.length === 0}
          >
            <span className="action-icon">🛑</span>
            <div className="action-text">
              <strong>Emergency Stop</strong>
              <p>Stop all trading bots</p>
            </div>
          </button>
        </div>
      </div>

      {/* Active Sessions Overview */}
      {activeSessions.length > 0 && (
        <div className="active-sessions-overview">
          <div className="section-header">
            <h3>Active Trading Sessions</h3>
            <button 
              className="view-all-button"
              onClick={() => setActiveTab('sessions')}
            >
              View All ({activeSessions.length})
            </button>
          </div>
          
          <div className="sessions-preview">
            {activeSessions.slice(0, 3).map(session => (
              <div key={session.sessionId} className="session-preview-card">
                <div className="session-preview-header">
                  <div className="token-info">
                    <strong>{session.tokenSymbol || 'Unknown'}</strong>
                    <div className="token-mint">
                      <CopyableMint 
                        mintAddress={session.tokenMint}
                        truncate={true}
                        truncateLength={8}
                      />
                    </div>
                  </div>
                  <div className={`session-status ${session.status}`}>
                    {session.status.replace('_', ' ')}
                  </div>
                </div>
                
                <div className="session-preview-metrics">
                  <div className="preview-metric">
                    <span className="label">P&L</span>
                    <span className={`value ${session.profitLoss >= 0 ? 'positive' : 'negative'}`}>
                      {formatPercentage(session.profitLoss)}
                    </span>
                  </div>
                  <div className="preview-metric">
                    <span className="label">Price</span>
                    <span className="value">
                      {session.currentPrice?.toFixed(6) || 'Loading...'}
                    </span>
                  </div>
                </div>

                {/* Real-time price monitor for this session */}
                <div className="session-price-monitor">
                  <PriceMonitor 
                    tokenMint={session.tokenMint}
                    onPriceUpdate={(priceData) => updateSessionPrices(session.tokenMint, priceData)}
                    showTrend={true}
                    showHistory={false}
                  />
                </div>
                
                <div className="session-preview-actions">
                  <button 
                    className="stop-session-button"
                    onClick={() => stopAutomatedTrading(session.sessionId)}
                    disabled={loading}
                  >
                    Stop
                  </button>
                  <button 
                    className="view-details-button"
                    onClick={() => {
                      setActiveTab('sessions');
                      // Scroll to specific session
                    }}
                  >
                    Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Alerts */}
      {alerts.length > 0 && (
        <div className="recent-alerts">
          <div className="section-header">
            <h3>Recent Alerts</h3>
            <button 
              className="view-all-button"
              onClick={() => setActiveTab('alerts')}
            >
              View All {unreadCount > 0 && <span className="alert-badge">{unreadCount}</span>}
            </button>
          </div>
          
          <div className="alerts-preview">
            {alerts.slice(0, 3).map(alert => (
              <div key={alert.id} className={`alert-preview ${alert.type} ${!alert.isRead ? 'unread' : ''}`}>
                <div className="alert-icon">
                  {alert.type === 'stop_loss_executed' && '🛡️'}
                  {alert.type === 'reentry_executed' && '🔄'}
                  {alert.type === 'insufficient_funds' && '💰'}
                  {alert.type === 'trading_session_started' && '🚀'}
                </div>
                <div className="alert-content">
                  <div className="alert-message">{alert.message}</div>
                  <div className="alert-time">
                    {new Date(alert.createdAt).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const handleEmergencyStop = async () => {
    if (window.confirm('Are you sure you want to stop ALL active trading sessions? This action cannot be undone.')) {
      try {
        setLoading(true);
        
        const stopPromises = activeSessions.map(session => 
          makeAuthenticatedRequest(`/trading/stop-bot/${session.sessionId}`, {
            method: 'POST'
          })
        );
        
        await Promise.all(stopPromises);
        await fetchActiveSessions();
        
        showSuccess(`Successfully stopped ${activeSessions.length} trading sessions`);
      } catch (error) {
        showError(`Failed to stop all sessions: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const renderManualTrading = () => (
    <div className="manual-trading-section">
      <div className="manual-trading-grid">
        <div className="jupiter-interface-container">
          <JupiterStyleTradingInterface />
        </div>
        <div className="trading-chart-panel">
          <div className="chart-panel-header">
            <h4>Market Price Chart</h4>
            <p>Real-time price action</p>
          </div>
          <div className="chart-panel-content">
            <PriceChart 
              tokenMint={manualTrade.tokenMint || 'So11111111111111111111111111111111111111112'}
              height={260}
              showControls={true}
            />
          </div>
        </div>
      </div>
      
      {/* Optional: Keep the old interface as a fallback or alternative */}
      <div className="classic-interface" style={{ marginTop: '32px' }}>
        <div className="interface-toggle">
          <h4>Classic Trading Interface</h4>
          <p>Alternative interface for advanced users</p>
        </div>
        
        <div className="trading-form">
          <div className="trade-type-selector">
            <button 
              className={`trade-type-button ${manualTrade.tradeType === 'buy' ? 'active buy' : ''}`}
              onClick={() => setManualTrade({ ...manualTrade, tradeType: 'buy' })}
            >
              Buy Token
            </button>
            <button 
              className={`trade-type-button ${manualTrade.tradeType === 'sell' ? 'active sell' : ''}`}
              onClick={() => setManualTrade({ ...manualTrade, tradeType: 'sell' })}
            >
              Sell Token
            </button>
          </div>

          <div className="form-group">
            <label>Token Address</label>
            <input
              type="text"
              value={manualTrade.tokenMint}
              onChange={(e) => setManualTrade({ ...manualTrade, tokenMint: e.target.value })}
              placeholder="Enter token mint address"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>
              {manualTrade.tradeType === 'buy' ? 'SOL Amount' : 'Token Amount'}
            </label>
            <input
              type="number"
              step="0.000001"
              value={manualTrade.amount}
              onChange={(e) => setManualTrade({ ...manualTrade, amount: e.target.value })}
              placeholder={manualTrade.tradeType === 'buy' ? '0.1' : '1000'}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Slippage Tolerance (%)</label>
            <select
              value={manualTrade.slippage}
              onChange={(e) => setManualTrade({ ...manualTrade, slippage: parseFloat(e.target.value) })}
              disabled={loading}
            >
              <option value={0.1}>0.1%</option>
              <option value={0.5}>0.5%</option>
              <option value={1.0}>1.0%</option>
              <option value={2.0}>2.0%</option>
              <option value={5.0}>5.0%</option>
            </select>
          </div>

          <div className="form-actions">
            <button 
              className="quote-button"
              onClick={getQuote}
              disabled={!manualTrade.tokenMint || !manualTrade.amount || !activeWallet || loading}
            >
              {loading ? 'Getting Quote...' : 'Get Quote'}
            </button>
          </div>

          {quote && (
            <div className="quote-section">
              <h4>Quote Details</h4>
              <div className="quote-details">
                <div className="quote-item">
                  <span className="label">You {manualTrade.tradeType}:</span>
                  <span className="value">
                    {manualTrade.tradeType === 'buy' 
                      ? `${formatAmount(quote.inputAmount, 9)} SOL`
                      : `${formatAmount(quote.inputAmount, 6)} Tokens`
                    }
                  </span>
                </div>
                <div className="quote-item">
                  <span className="label">You receive:</span>
                  <span className="value">
                    {manualTrade.tradeType === 'buy' 
                      ? `${formatAmount(quote.outputAmount, 6)} Tokens`
                      : `${formatAmount(quote.outputAmount, 9)} SOL`
                    }
                  </span>
                </div>
                <div className="quote-item">
                  <span className="label">Price Impact:</span>
                  <span className={`value ${quote.priceImpact > 5 ? 'high-impact' : ''}`}>
                    {formatPercentage(quote.priceImpact)}
                  </span>
                </div>
              </div>

              <button 
                className={`execute-button ${manualTrade.tradeType}`}
                onClick={executeManualTrade}
                disabled={loading || !activeWallet}
              >
                {loading ? 'Executing...' : `Execute ${manualTrade.tradeType.toUpperCase()}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderAutomatedTrading = () => (
    <div className="automated-trading-section">
      <h3>Automated Trading Bot</h3>
      <p>Start automated trading with stop-loss, re-entry, and trend detection</p>

      <div className="bot-config-form">
        <div className="form-group">
          <label>Token Address</label>
          <input
            type="text"
            value={botConfig.tokenMint}
            onChange={(e) => setBotConfig({ ...botConfig, tokenMint: e.target.value })}
            placeholder="Enter token mint address"
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label>Initial SOL Amount</label>
          <input
            type="number"
            step="0.01"
            min="0.005"
            value={botConfig.initialAmount}
            onChange={(e) => setBotConfig({ ...botConfig, initialAmount: e.target.value })}
            placeholder="0.1"
            disabled={loading}
          />
          <div className="field-help">
            <p>Minimum 0.005 SOL required to start automated trading</p>
          </div>
        </div>

        <div className="form-group">
          <label>Stop-Loss Configuration</label>
          <div className="stop-loss-config">
            <div className="stop-loss-percentage">
              <label className="sub-label">Stop-Loss Percentage</label>
              <div className="percentage-input-wrapper">
                <input
                  type="number"
                  className="percentage-input"
                  value={botConfig.stopLossPercentage}
                  onChange={(e) => setBotConfig({ ...botConfig, stopLossPercentage: parseFloat(e.target.value) || 10 })}
                  min="1"
                  max="50"
                  step="1"
                  disabled={loading}
                />
                <span className="percentage-suffix">%</span>
              </div>
              <div className="field-help">
                <p>Sell when price drops by this percentage from highest point</p>
              </div>
            </div>
            
            <div className="trailing-stop-toggle">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={botConfig.trailingStopEnabled}
                  onChange={(e) => setBotConfig({ ...botConfig, trailingStopEnabled: e.target.checked })}
                  disabled={loading}
                />
                <span className="checkbox-text">Enable Trailing Stop-Loss</span>
              </label>
              <div className="field-help">
                <p>
                  {botConfig.trailingStopEnabled 
                    ? "Stop-loss follows price up, protecting profits" 
                    : "Stop-loss stays at initial purchase price level"
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Wallet</label>
          <select
            value={botConfig.walletId}
            onChange={(e) => setBotConfig({ ...botConfig, walletId: e.target.value })}
            disabled={loading}
          >
            <option value="">Select a wallet</option>
            {wallets.filter(w => w.wallet_type === 'platform').map(wallet => (
              <option key={wallet.id} value={wallet.id}>
                {wallet.name} ({wallet.wallet_address.slice(0, 8)}...)
              </option>
            ))}
          </select>
        </div>

        <div className="bot-features">
          <h4>Bot Features</h4>
          <div className="feature-list">
            <div className="feature-item">
              <span className="feature-icon">🛡️</span>
              <div className="feature-text">
                <strong>Trailing Stop-Loss</strong>
                <p>Sell at {botConfig.stopLossPercentage}% drop from highest price reached</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📈</span>
              <div className="feature-text">
                <strong>Profit Protection</strong>
                <p>Stop-loss follows price up to lock in gains automatically</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🔄</span>
              <div className="feature-text">
                <strong>Smart Re-Entry</strong>
                <p>Buys back when price recovers after stop-loss execution</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🔁</span>
              <div className="feature-text">
                <strong>Retry Logic</strong>
                <p>Automatically retries failed transactions with exponential backoff</p>
              </div>
            </div>
          </div>
        </div>

        <button 
          className="start-bot-button"
          onClick={startAutomatedTrading}
          disabled={!botConfig.tokenMint || !botConfig.initialAmount || !botConfig.walletId || loading}
        >
          {loading ? 'Starting Bot...' : 'Start Automated Trading'}
        </button>
      </div>
    </div>
  );

  const renderActiveSessions = () => (
    <div className="active-sessions-section">
      <div className="section-header">
        <h3>Active Trading Sessions</h3>
        <button 
          className="refresh-button"
          onClick={fetchActiveSessions}
          disabled={loading}
          title="Refresh sessions"
        >
          🔄 Refresh
        </button>
      </div>
      <p>Monitor and manage your automated trading bots</p>

      {activeSessions.length === 0 ? (
        <div className="no-sessions">
          <div className="empty-state-icon">🤖</div>
          <p>No active trading sessions. Start automated trading to see sessions here.</p>
          <button 
            className="start-first-bot-button"
            onClick={() => setActiveTab('automated')}
          >
            Start Your First Bot
          </button>
        </div>
      ) : (
        <div className="sessions-grid">
          {activeSessions.map(session => (
            <div key={session.sessionId} className="session-card">
              <div className="session-header">
                <div className="session-info">
                  <h4>{session.tokenSymbol || 'Unknown Token'}</h4>
                  <div className="token-mint">
                    <CopyableMint 
                      mintAddress={session.tokenMint}
                      truncate={true}
                      truncateLength={8}
                    />
                  </div>
                </div>
                <div className={`session-status ${session.status}`}>
                  {session.status.replace('_', ' ')}
                </div>
              </div>

              <div className="session-metrics">
                <div className="metric">
                  <span className="metric-label">Initial Price</span>
                  <span className="metric-value">{session.initialPrice?.toFixed(6)} SOL</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Current Price</span>
                  <span className="metric-value">{session.currentPrice?.toFixed(6)} SOL</span>
                </div>
                <div className="metric">
                  <span className="metric-label">P&L</span>
                  <span className={`metric-value ${session.profitLoss >= 0 ? 'profit' : 'loss'}`}>
                    {formatPercentage(session.profitLoss)}
                  </span>
                </div>
              </div>

              {/* Price Chart for this session */}
              <div className="session-price-chart">
                <PriceChart 
                  tokenMint={session.tokenMint}
                  height={150}
                  showControls={false}
                />
              </div>

              {/* Real-time price monitor */}
              <div className="session-real-time-monitor">
                <PriceMonitor 
                  tokenMint={session.tokenMint}
                  onPriceUpdate={(priceData) => updateSessionPrices(session.tokenMint, priceData)}
                  showTrend={true}
                  showHistory={false}
                />
              </div>

              {session.status === 'active' && (
                <div className="session-features">
                  <div className="feature-indicator active">
                    <span className="indicator-dot"></span>
                    Stop-Loss Active (10%)
                  </div>
                  <div className="feature-indicator active">
                    <span className="indicator-dot"></span>
                    Re-Entry Monitoring
                  </div>
                  <div className="feature-indicator active">
                    <span className="indicator-dot"></span>
                    Trend Detection
                  </div>
                </div>
              )}

              <div className="session-actions">
                <button 
                  className="stop-session-button"
                  onClick={() => stopAutomatedTrading(session.sessionId)}
                  disabled={loading}
                >
                  {loading ? 'Stopping...' : 'Stop Trading'}
                </button>
                <button 
                  className="view-details-button"
                  onClick={() => fetchSessionDetails(session.sessionId)}
                >
                  View Details
                </button>
              </div>

              <div className="session-timestamp">
                Started: {new Date(session.createdAt).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderAlerts = () => (
    <div className="alerts-section">
      <div className="alerts-header">
        <h3>Trading Alerts {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}</h3>
        {unreadCount > 0 && (
          <button 
            className="mark-read-button"
            onClick={() => markAlertsAsRead()}
            disabled={loading}
          >
            Mark All Read
          </button>
        )}
      </div>
      <p>Stay informed about your trading bot activities</p>

      {alerts.length === 0 ? (
        <div className="no-alerts">
          <p>No alerts yet. Trading alerts will appear here when your bots take actions.</p>
        </div>
      ) : (
        <div className="alerts-list">
          {alerts.map(alert => (
            <div key={alert.id} className={`alert-item ${alert.type} ${!alert.isRead ? 'unread' : ''}`}>
              <div className="alert-icon">
                {alert.type === 'stop_loss_executed' && '🛡️'}
                {alert.type === 'reentry_executed' && '🔄'}
                {alert.type === 'insufficient_funds' && '💰'}
                {alert.type === 'stop_loss_failed' && '❌'}
                {alert.type === 'reentry_failed' && '🔄'}
                {alert.type === 'trading_session_started' && '🚀'}
                {alert.type === 'trading_session_stopped' && '⏹️'}
              </div>
              <div className="alert-content">
                <div className="alert-message">{alert.message}</div>
                <div className="alert-timestamp">
                  {new Date(alert.createdAt).toLocaleString()}
                </div>
              </div>
              {!alert.isRead && (
                <button 
                  className="mark-single-read"
                  onClick={() => markAlertsAsRead([alert.id])}
                  title="Mark as read"
                >
                  ✓
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Debug: Simple test return to verify component loading
  if (process.env.NODE_ENV === 'development') {
    console.log('🧪 TradingDashboard activeTab:', activeTab);
  }

  return (
    <div className="trading-dashboard-layout">
      {!hideGlobalSidebar && (
        <>
          <MobileMenuToggle 
            isOpen={isMobileMenuOpen}
            onToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          />
          
          <Sidebar 
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            activeSessions={activeSessions}
            alerts={alerts}
            isMobileOpen={isMobileMenuOpen}
            onMobileClose={() => setIsMobileMenuOpen(false)}
          />
        </>
      )}
      
      <div className={`main-content ${hideGlobalSidebar ? 'no-global-sidebar' : ''}`}>
        {activeTab === 'dashboard' && (
          <EnhancedDashboard
            realTimeData={realTimeData}
            activeSessions={activeSessions}
            alerts={alerts}
            tradeHistory={tradeHistory}
            makeAuthenticatedRequest={makeAuthenticatedRequest}
            setCurrentView={setCurrentView}
          />
        )}
        {activeTab === 'manual' && (
          <div className="content-section">
            <div className="section-header">
              <h2>⚡ Manual Trading</h2>
              <p>Execute trades manually with real-time market data</p>
              <WalletSelector />
            </div>
            <div className="section-content">
              {renderManualTrading()}
            </div>
          </div>
        )}
        {activeTab === 'automated' && (
          <div className="content-section">
            <div className="section-header">
              <h2>🤖 Automated Trading</h2>
              <p>Set up intelligent trading bots with advanced features</p>
              <WalletSelector />
            </div>
            <div className="section-content">
              {renderAutomatedTrading()}
            </div>
          </div>
        )}
        {activeTab === 'sessions' && (
          <div className="content-section">
            <div className="section-header">
              <h2>🔄 Active Trading Sessions</h2>
              <p>Monitor and manage your live trading bots</p>
              <WalletSelector />
            </div>
            <div className="section-content">
              {renderActiveSessions()}
            </div>
          </div>
        )}
        {activeTab === 'alerts' && (
          <div className="content-section">
            <div className="section-header">
              <h2>🔔 Trading Alerts</h2>
              <p>Stay informed about your trading activities</p>
            </div>
            <div className="section-content">
              {renderAlerts()}
            </div>
          </div>
        )}
      </div>



      {/* Session Details Modal */}
      {showSessionModal && selectedSessionDetails && (
        <div className="modal-overlay" onClick={() => setShowSessionModal(false)}>
          <div className="session-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Trading Session Details</h3>
              <button 
                className="close-modal-btn"
                onClick={() => setShowSessionModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="session-detail-grid">
                <div className="detail-section">
                  <h4>Session Information</h4>
                  <div className="detail-item">
                    <span className="label">Session ID:</span>
                    <span className="value">{selectedSessionDetails.sessionId}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Token:</span>
                    <span className="value">{selectedSessionDetails.tokenSymbol || 'Unknown'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Token Address:</span>
                    <span className="value">{selectedSessionDetails.tokenMint}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Status:</span>
                    <span className={`value status-${selectedSessionDetails.status}`}>
                      {selectedSessionDetails.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Started:</span>
                    <span className="value">
                      {new Date(selectedSessionDetails.createdAt).toLocaleString()}
                    </span>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Price Information</h4>
                  <div className="detail-item">
                    <span className="label">Initial Price:</span>
                    <span className="value">{selectedSessionDetails.initialPrice?.toFixed(6)} SOL</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Current Price:</span>
                    <span className="value">{selectedSessionDetails.currentPrice?.toFixed(6)} SOL</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Highest Price:</span>
                    <span className="value">{selectedSessionDetails.highestPrice?.toFixed(6)} SOL</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Stop-Loss Price:</span>
                    <span className="value">{selectedSessionDetails.stopLossPrice?.toFixed(6)} SOL</span>
                  </div>
                  {selectedSessionDetails.reentryPrice && (
                    <div className="detail-item">
                      <span className="label">Re-entry Price:</span>
                      <span className="value">{selectedSessionDetails.reentryPrice?.toFixed(6)} SOL</span>
                    </div>
                  )}
                </div>

                <div className="detail-section">
                  <h4>Performance</h4>
                  <div className="detail-item">
                    <span className="label">Profit/Loss:</span>
                    <span className={`value ${selectedSessionDetails.profitLoss >= 0 ? 'profit' : 'loss'}`}>
                      {formatPercentage(selectedSessionDetails.profitLoss)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Total Trades:</span>
                    <span className="value">{selectedSessionDetails.totalTrades || 0}</span>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Bot Configuration</h4>
                  <div className="detail-item">
                    <span className="label">Stop-Loss Percentage:</span>
                    <span className="value">{selectedSessionDetails.stopLossPercentage || 10}%</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Trailing Stop-Loss:</span>
                    <span className="value">{selectedSessionDetails.trailingStopEnabled !== false ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Re-entry Enabled:</span>
                    <span className="value">Yes</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Trend Detection:</span>
                    <span className="value">Active</span>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  className="stop-session-btn"
                  onClick={() => {
                    stopAutomatedTrading(selectedSessionDetails.sessionId);
                    setShowSessionModal(false);
                  }}
                  disabled={loading || selectedSessionDetails.status !== 'active'}
                >
                  Stop Session
                </button>
                <button 
                  className="close-btn"
                  onClick={() => setShowSessionModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingDashboard;